import React, { useState } from "react";
import {
  Check,
  ArrowRight,
  TestTube,
  Building2,
  Activity,
  HeartHandshake,
  Sparkles,
} from "lucide-react";

function scrollTo(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

const PRIVATE_PLANS = [
  {
    key: "start",
    name: "Start",
    duration: "1 Monat",
    price: "660",
    period: "inkl. MwSt.",
    monthly: null,
    description:
      "Der ideale Einstieg – wenn du in vier Wochen Klarheit über Training und deine Nährstoffversorgung willst.",
    features: [
      "4× Personal Training",
      "Mikronährstoffanalyse",
      "Mikronährstoffempfehlung",
      "Fortlaufendes Ernährungscoaching",
      "1 Trainingsplan",
      "E-Learning Plattform",
    ],
    note: "zzgl. einmalig 55 € Laborkosten bei Bluttest",
  },
  {
    key: "prozess",
    name: "Prozess",
    duration: "3 Monate",
    price: "1800",
    period: "inkl. MwSt.",
    monthly: "600 €/Monat",
    description:
      "Der Klassiker – genug Zeit, um Trainingsroutinen zu verankern und Fortschritte spürbar zu machen.",
    features: [
      "12× Personal Training",
      "2× Mikronährstoffanalyse",
      "2× Mikronährstoffempfehlung",
      "Fortlaufendes Ernährungscoaching",
      "3 Trainingspläne",
      "E-Learning Plattform",
    ],
    note: "zzgl. zweimal 55 € Laborkosten bei Bluttest",
  },
  {
    key: "kontinuitaet",
    name: "Kontinuität",
    duration: "6 Monate",
    price: "3300",
    period: "inkl. MwSt.",
    monthly: "550 €/Monat",
    description:
      "Die tiefgreifende Begleitung – für nachhaltige Veränderung, die auch nach dem Programm bleibt.",
    features: [
      "25× Personal Training",
      "2× Mikronährstoffanalyse",
      "2× Mikronährstoffempfehlung",
      "Fortlaufendes Ernährungscoaching",
      "6 Trainingspläne",
      "E-Learning Plattform",
    ],
    note: "zzgl. zweimal 55 € Laborkosten bei Bluttest",
  },
];

const STANDALONE = {
  name: "Nur Mikronährstoffberatung",
  price: "60",
  period: "€",
  description:
    "Du willst erstmal nur den Mikronährstoff-Teil – ohne Training? Auch das geht. Einzelberatung inkl. Analyse und Empfehlung.",
  note: "zzgl. einmalig 55 € Laborkosten bei Bluttest",
};

const BUSINESS_FEATURES = [
  {
    icon: Activity,
    title: "Bewegungsanalyse am Arbeitsplatz",
    text: "Analyse typischer Bewegungsabläufe direkt vor Ort – um Belastungen zu reduzieren und Beschwerden vorzubeugen.",
  },
  {
    icon: HeartHandshake,
    title: "Individuelle Mitarbeitenden-Begleitung",
    text: "Mitarbeitende erhalten konkrete Strategien, um ihre Leistungsfähigkeit langfristig zu erhalten.",
  },
  {
    icon: Sparkles,
    title: "Sportkurse für Teams",
    text: "Sportkurse, die Freude an Bewegung vermitteln und einen aktiven, gesunden Lebensstil im Team verankern.",
  },
];

const TABS = [
  { key: "private", label: "Für Privatpersonen" },
  { key: "business", label: "Für Unternehmen" },
];

export default function ServicesSection() {
  const [tab, setTab] = useState("private");

  return (
    <section
      id="services-section"
      data-testid="services-section"
      className="bg-[#F7F9FB] text-[#1F2937] py-24 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Heading */}
        <div className="max-w-3xl mb-10">
          <p className="text-xs sm:text-sm font-semibold tracking-[0.18em] uppercase text-[#2E89C4] mb-5">
            Leistungen
          </p>
          <h2
            data-testid="services-title"
            className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.1]"
          >
            Klare Pakete – persönlich begleitet.
          </h2>
          <p className="mt-5 text-base lg:text-lg text-[#1F2937]/70 leading-relaxed">
            Egal ob du privat an dir arbeiten oder dein Team im Unternehmen
            stärken willst – wähle, was zu dir passt.
          </p>
        </div>

        {/* Tabs */}
        <div
          data-testid="services-tabs"
          className="inline-flex p-1.5 rounded-full bg-white border border-[#1F2937]/10 shadow-sm mb-12"
        >
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`services-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-5 sm:px-7 py-2.5 rounded-full text-sm font-semibold transition-all ${
                tab === t.key
                  ? "bg-[#1F2937] text-[#F7F9FB] shadow-sm"
                  : "text-[#1F2937]/65 hover:text-[#1F2937]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* PRIVATE */}
        {tab === "private" && (
          <div data-testid="services-private-panel" className="space-y-10">
            <div className="grid md:grid-cols-3 gap-5 lg:gap-6">
              {PRIVATE_PLANS.map((p, i) => (
                <article
                  key={p.key}
                  data-testid={`plan-${p.key}`}
                  className="relative rounded-2xl p-7 lg:p-8 flex flex-col transition-all duration-300 hover:-translate-y-1 bg-white text-[#1F2937] border border-[#1F2937]/10 hover:border-[#2E89C4]/40 hover:shadow-[0_20px_50px_-25px_rgba(31,41,55,0.35)]"
                  style={{ order: i }}
                >
                  <div className="mb-5">
                    <div className="flex items-baseline justify-between gap-3 mb-2">
                      <h3 className="font-display text-2xl font-bold">
                        {p.name}
                      </h3>
                      <span className="text-[11px] font-semibold tracking-[0.14em] uppercase text-[#6FB04F]">
                        {p.duration}
                      </span>
                    </div>
                    <p className="text-sm leading-relaxed text-[#1F2937]/65">
                      {p.description}
                    </p>
                  </div>

                  <div className="mb-6">
                    <div className="flex items-baseline gap-1">
                      <span className="font-display text-5xl font-extrabold">
                        {p.price}
                      </span>
                      <span className="text-sm font-semibold">€</span>
                      <span className="text-xs text-[#1F2937]/55 ml-2">
                        {p.period}
                      </span>
                    </div>
                    {p.monthly && (
                      <p className="text-xs text-[#6FB04F] font-semibold mt-1.5">
                        entspricht {p.monthly}
                      </p>
                    )}
                  </div>

                  <ul className="space-y-3 mb-7 flex-1">
                    {p.features.map((f, j) => (
                      <li
                        key={j}
                        className="flex items-start gap-2.5 text-[14.5px]"
                      >
                        <span className="mt-0.5 h-5 w-5 shrink-0 rounded-full flex items-center justify-center bg-[#6FB04F]/15">
                          <Check
                            size={12}
                            strokeWidth={3}
                            className="text-[#6FB04F]"
                          />
                        </span>
                        <span className="text-[#1F2937]/85">{f}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    data-testid={`plan-cta-${p.key}`}
                    onClick={() => scrollTo("booking-section")}
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold bg-[#1F2937] text-[#F7F9FB] hover:bg-[#0F1825] transition-all"
                  >
                    Erstgespräch buchen
                    <ArrowRight size={15} />
                  </button>

                  <p className="mt-4 text-[11px] text-[#1F2937]/45">
                    {p.note}
                  </p>
                </article>
              ))}
            </div>

            {/* Standalone Mikronährstoff */}
            <div
              data-testid="plan-standalone"
              className="rounded-2xl border border-dashed border-[#2E89C4]/40 bg-white p-7 lg:p-8 flex flex-col lg:flex-row lg:items-center gap-6"
            >
              <div className="h-14 w-14 shrink-0 rounded-2xl bg-[#2E89C4]/10 flex items-center justify-center">
                <TestTube size={22} className="text-[#2E89C4]" strokeWidth={1.8} />
              </div>
              <div className="flex-1">
                <h3 className="font-display text-xl font-bold">
                  {STANDALONE.name}
                </h3>
                <p className="mt-1.5 text-sm text-[#1F2937]/65 leading-relaxed max-w-2xl">
                  {STANDALONE.description}
                </p>
                <p className="mt-2 text-[11px] text-[#1F2937]/45">
                  {STANDALONE.note}
                </p>
              </div>
              <div className="flex items-center gap-5 lg:flex-col lg:items-end lg:gap-3">
                <div className="flex flex-col items-start lg:items-end">
                  <div className="flex items-baseline gap-1">
                    <span className="font-display text-4xl font-extrabold text-[#1F2937]">
                      {STANDALONE.price}
                    </span>
                    <span className="text-sm font-semibold">€</span>
                  </div>
                  <span className="text-xs text-[#1F2937]/55 mt-0.5">
                    inkl. MwSt.
                  </span>
                </div>
                <button
                  data-testid="plan-cta-standalone"
                  onClick={() => scrollTo("booking-section")}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold bg-[#2E89C4] text-white hover:bg-[#1F6FA1] transition"
                >
                  Termin sichern
                  <ArrowRight size={15} />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* BUSINESS */}
        {tab === "business" && (
          <div data-testid="services-business-panel" className="grid lg:grid-cols-12 gap-8 lg:gap-12">
            <div className="lg:col-span-5">
              <div className="rounded-3xl bg-[#1F2937] text-[#F7F9FB] p-8 lg:p-10 h-full flex flex-col">
                <div className="h-12 w-12 rounded-xl bg-[#2E89C4]/20 flex items-center justify-center mb-6">
                  <Building2 size={22} className="text-[#2E89C4]" strokeWidth={1.8} />
                </div>
                <h3 className="font-display text-2xl lg:text-3xl font-bold leading-tight">
                  Betriebliches Gesundheitsmanagement
                </h3>
                <p className="mt-5 text-[#F7F9FB]/70 text-[15px] leading-relaxed">
                  Ich analysiere typische Bewegungsabläufe direkt am
                  Arbeitsplatz und unterstütze Mitarbeitende dabei, Belastungen
                  zu reduzieren, Beschwerden vorzubeugen und ihre
                  Leistungsfähigkeit langfristig zu erhalten. Zusätzlich biete
                  ich Sportkurse an, um die Freude an Bewegung zu vermitteln
                  und einen aktiven, gesunden Lebensstil im Team zu verankern.
                </p>

                <div className="mt-auto pt-8">
                  <p className="text-xs font-semibold tracking-[0.18em] uppercase text-[#6FB04F] mb-2">
                    Investment
                  </p>
                  <p className="font-display text-3xl font-extrabold">
                    Preis auf Anfrage
                  </p>
                  <p className="text-sm text-[#F7F9FB]/55 mt-1.5">
                    Individuell auf Unternehmensgröße und Bedarf abgestimmt.
                  </p>

                  <button
                    data-testid="plan-cta-business"
                    onClick={() => scrollTo("contact-form-section")}
                    className="mt-7 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold bg-[#2E89C4] text-white hover:bg-[#1F6FA1] transition w-full sm:w-auto"
                  >
                    Unverbindliches Angebot anfragen
                    <ArrowRight size={15} />
                  </button>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7 space-y-4">
              {BUSINESS_FEATURES.map((b, i) => {
                const Icon = b.icon;
                return (
                  <div
                    key={i}
                    data-testid={`business-feature-${i}`}
                    className="rounded-2xl border border-[#1F2937]/10 bg-white p-6 lg:p-7 flex items-start gap-5 transition-all hover:-translate-y-0.5 hover:border-[#2E89C4]/30 hover:shadow-[0_15px_40px_-20px_rgba(31,41,55,0.3)]"
                  >
                    <div className="h-12 w-12 shrink-0 rounded-xl bg-[#6FB04F]/12 flex items-center justify-center">
                      <Icon size={20} className="text-[#6FB04F]" strokeWidth={1.8} />
                    </div>
                    <div>
                      <h4 className="font-display text-lg font-bold">
                        {b.title}
                      </h4>
                      <p className="mt-2 text-[14.5px] text-[#1F2937]/70 leading-relaxed">
                        {b.text}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
